import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { pitchUsageTypeLabel } from "@/lib/pitchUsageType";
import { isDevDataDeletionHost } from "@/lib/platform";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { BulkCreatePitchModal } from "@/pages/pitches/BulkCreatePitchModal";
import { BulkEditPitchModal } from "@/pages/pitches/BulkEditPitchModal";
import { CreatePitchModal } from "@/pages/pitches/CreatePitchModal";
import { EditPitchModal } from "@/pages/pitches/EditPitchModal";
import { PitchFeaturesModal } from "@/pages/pitches/PitchFeaturesModal";
import type { Pitch, PitchCategory, PitchFeature } from "@/types/camping";

function amenity(p: Pitch, key: "has_electricity" | "has_water" | "has_sewage") {
  return p[key] ? (
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-status-green/15 text-xs text-status-green">
      ✓
    </span>
  ) : (
    <span className="text-label-tertiary">—</span>
  );
}

export function PitchesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const isDev = isDevDataDeletionHost();
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [features, setFeatures] = useState<PitchFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPitch, setSelectedPitch] = useState<Pitch | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const pitchParams = showInactive ? "?include_inactive=true" : "?include_inactive=false";
    Promise.all([
      api.get<Pitch[]>(`/api/v1/pitches${pitchParams}`),
      api.get<PitchCategory[]>("/api/v1/pitch-categories"),
      api.get<PitchFeature[]>("/api/v1/pitch-features"),
    ])
      .then(([pitchList, categoryList, featureList]) => {
        setPitches(pitchList);
        setCategories(categoryList);
        setFeatures(featureList);
        setSelectedIds(new Set());
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [showInactive]);

  const featureNames = useMemo(
    () => new Map(features.map((f) => [f.id, f.name])),
    [features],
  );

  const selectedPitches = useMemo(
    () => pitches.filter((p) => selectedIds.has(p.id)),
    [pitches, selectedIds],
  );
  const selectedActiveCount = useMemo(
    () => selectedPitches.filter((p) => p.is_active).length,
    [selectedPitches],
  );

  const allSelected = pitches.length > 0 && selectedIds.size === pitches.length;
  const someSelected = selectedIds.size > 0;

  async function handleBulkSoftDelete() {
    if (!canEdit || selectedActiveCount === 0) return;
    const confirmed = window.confirm(
      `${selectedActiveCount} Stellplatz${selectedActiveCount === 1 ? "" : "e"} ausblenden?\n\nDie Plätze bleiben in der Datenbank erhalten, erscheinen aber nicht mehr in der Platzliste und sind nicht mehr buchbar. Bestehende Buchungen bleiben unverändert.`,
    );
    if (!confirmed) return;
    setBulkDeleting(true);
    setError(null);
    try {
      const res = await api.post<{
        deactivated: number;
        errors: { message: string }[];
        message: string;
      }>("/api/v1/pitches/bulk/deactivate", {
        pitch_ids: selectedPitches.map((p) => p.id),
      });
      if (res.deactivated > 0) load();
      if (res.errors.length > 0) {
        setError(res.errors.map((e) => e.message).join("\n"));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Löschen fehlgeschlagen");
    } finally {
      setBulkDeleting(false);
    }
  }

  async function handleBulkHardDelete() {
    if (!canEdit || !isDev || selectedPitches.length === 0) return;
    const codes =
      selectedPitches.length <= 6
        ? selectedPitches.map((p) => p.code).join(", ")
        : `${selectedPitches
            .slice(0, 5)
            .map((p) => p.code)
            .join(", ")} … (+${selectedPitches.length - 5})`;
    const first = window.confirm(
      `${selectedPitches.length} Stellplatz${selectedPitches.length === 1 ? "" : "e"} ENDGÜLTIG aus der Datenbank löschen?\n\nNur in der Dev-Umgebung. Dies kann nicht rückgängig gemacht werden.`,
    );
    if (!first) return;
    const second = window.confirm(`Wirklich löschen? Codes: ${codes}`);
    if (!second) return;
    setBulkDeleting(true);
    setError(null);
    try {
      const res = await api.post<{
        deleted: number;
        errors: { message: string }[];
        message: string;
      }>("/api/v1/dev/pitches/bulk-delete", {
        pitch_ids: selectedPitches.map((p) => p.id),
      });
      if (res.deleted > 0) load();
      if (res.errors.length > 0) {
        setError(res.errors.map((e) => e.message).join("\n"));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Endgültiges Löschen fehlgeschlagen");
    } finally {
      setBulkDeleting(false);
    }
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pitches.map((p) => p.id)));
    }
  }

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Stellplätze" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  return (
    <div className="space-y-6">
      <EditPitchModal
        open={!!selectedPitch}
        pitch={selectedPitch}
        categories={categories}
        onClose={() => setSelectedPitch(null)}
        onSaved={load}
        onDeleted={load}
        canEdit={canEdit}
      />
      <CreatePitchModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        categories={categories}
      />
      <BulkCreatePitchModal
        open={bulkCreateOpen}
        onClose={() => setBulkCreateOpen(false)}
        onCreated={load}
        categories={categories}
      />
      <BulkEditPitchModal
        open={bulkEditOpen}
        pitches={selectedPitches}
        categories={categories}
        canEdit={canEdit}
        onClose={() => setBulkEditOpen(false)}
        onSaved={load}
        onDeleted={load}
      />
      <PitchFeaturesModal
        open={featuresOpen}
        onClose={() => setFeaturesOpen(false)}
        onChanged={load}
        canEdit={canEdit}
      />

      <label className="flex items-center gap-2 text-sm text-label-secondary">
        <input
          type="checkbox"
          className="rounded border-separator text-accent focus:ring-accent"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        Ausgeblendete Plätze anzeigen
      </label>

      {canEdit ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {someSelected ? (
            <>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => setBulkEditOpen(true)}
                disabled={bulkDeleting}
              >
                {selectedIds.size} Plätze bearbeiten
              </Button>
              {selectedActiveCount > 0 ? (
                <Button
                  type="button"
                  variant="danger"
                  className="w-full sm:w-auto"
                  onClick={() => void handleBulkSoftDelete()}
                  loading={bulkDeleting}
                >
                  {selectedActiveCount} Plätze ausblenden
                </Button>
              ) : null}
              {isDev ? (
                <Button
                  type="button"
                  variant="danger"
                  className="w-full sm:w-auto"
                  onClick={() => void handleBulkHardDelete()}
                  loading={bulkDeleting}
                >
                  Endgültig löschen (Dev)
                </Button>
              ) : null}
            </>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={() => setFeaturesOpen(true)}
          >
            Eigenschaften verwalten
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={() => setBulkCreateOpen(true)}
          >
            Mehrere anlegen
          </Button>
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Neuer Stellplatz
          </Button>
        </div>
      ) : null}

      {error && <p className="text-sm text-status-red">{error}</p>}
      {loading ? (
        <p className="text-sm text-label-secondary">Lade Plätze…</p>
      ) : (
        <Table wide emptyMessage="Noch keine Stellplätze angelegt." isEmpty={pitches.length === 0}>
          <thead>
            <tr>
              {canEdit ? (
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="rounded border-separator text-accent focus:ring-accent"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Alle Stellplätze auswählen"
                  />
                </th>
              ) : null}
              <th>Code</th>
              <th>Typ</th>
              <th>Kategorie</th>
              <th>m²</th>
              <th>Strom</th>
              <th>Wasser</th>
              <th>Abwasser</th>
              <th>Eigenschaften</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pitches.map((p) => (
              <ClickableTableRow
                key={p.id}
                ariaLabel={`Stellplatz ${p.code} öffnen`}
                onActivate={() => setSelectedPitch(p)}
              >
                {canEdit ? (
                  <td data-label="Auswahl" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="rounded border-separator text-accent focus:ring-accent"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleOne(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Stellplatz ${p.code} auswählen`}
                    />
                  </td>
                ) : null}
                <td className="font-medium" data-label="Code">
                  {p.code}
                </td>
                <td className="text-label-secondary" data-label="Typ">
                  {pitchUsageTypeLabel(p.usage_type)}
                </td>
                <td className="text-label-secondary" data-label="Kategorie">
                  {p.category_name ?? "—"}
                </td>
                <td className="tabular-nums text-label-secondary" data-label="m²">
                  {p.size_sqm ?? "—"}
                </td>
                <td data-label="Strom">
                  {amenity(p, "has_electricity")}
                  {p.fernauslese_channel != null ? (
                    <span className="ml-1 text-xs text-label-tertiary" title="Fernauslese Kanal">
                      (K{p.fernauslese_channel})
                    </span>
                  ) : null}
                </td>
                <td data-label="Wasser">{amenity(p, "has_water")}</td>
                <td data-label="Abwasser">{amenity(p, "has_sewage")}</td>
                <td className="text-label-secondary" data-label="Eigenschaften">
                  {(p.feature_ids ?? [])
                    .map((id) => featureNames.get(id))
                    .filter(Boolean)
                    .join(", ") || "—"}
                </td>
                <td className="text-label-secondary" data-label="Status">
                  {!p.is_active ? (
                    <span className="text-status-orange">Ausgeblendet</span>
                  ) : p.is_blocked ? (
                    "Gesperrt"
                  ) : (
                    "Aktiv"
                  )}
                </td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
